import * as React from 'react';
import { FieldPath, FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { SelectProps } from '../Select/Select';
import { TextField, TextFieldProps } from '../TextField/TextField';

export interface SelectFormItemProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  // -- UseControllerProps -- //
  name: UseControllerProps<TFieldValues, TName>['name'];
  control: UseControllerProps<TFieldValues>['control'];
  /**
   * @default null
   */
  defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue'];

  // -- TextFieldProps -- //
  variant?: TextFieldProps['variant'];
  disabled?: TextFieldProps['disabled'];
  label: TextFieldProps['label'];
  children: NonNullable<TextFieldProps['children']>;
  fullWidth?: TextFieldProps['fullWidth'];
  onEnter?: TextFieldProps['onEnter'];
  onChange?: TextFieldProps['onChange'];
  required?: TextFieldProps['required'];
  sx?: TextFieldProps['sx'];

  // -- Select Props -- //
  renderValue?: SelectProps['renderValue'];
}

export const SelectMultipleFormItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: SelectFormItemProps<TFieldValues, TName>
) => {
  const {
    name,
    control,
    defaultValue,
    onChange: onChangeFromProps,
    onEnter,
    renderValue,
    disabled,
    variant,
    children,
    required,
    fullWidth,
    label,
    sx
  } = props;

  const {
    field: { ref, ...field },
    fieldState: { error }
  } = useController({
    name,
    control,
    defaultValue: defaultValue
  });

  const { onChange, value, ...restOfField } = field;

  return (
    <TextField
      value={value || []}
      error={!!error}
      helperText={error?.message ?? ''}
      onChange={(e) => {
        onChange(e);
        if (onChangeFromProps) onChangeFromProps(e);
      }}
      onEnter={onEnter}
      inputRef={ref}
      label={label}
      required={required}
      disabled={disabled}
      variant={variant}
      fullWidth={fullWidth !== undefined ? fullWidth : true}
      margin='dense'
      select
      slotProps={{
        select: {
          multiple: true,
          renderValue
        }
      }}
      sx={sx}
      {...restOfField}>
      {children}
    </TextField>
  );
};
